import React, { useState, useEffect, useRef, useCallback } from 'react';
import Header from '../../components/admin/Header';
import Sidebar from '../../components/admin/Sidebar';
import Footer from '../../components/admin/Footer';
import axios from 'axios';
import { 
  Save, 
  Upload, 
  X, 
  Loader,
  Link as LinkIcon,
  Type,
  AlignLeft,
  Hash,
  FolderOpen,
  AlertCircle,
  CheckCircle
} from 'lucide-react';

/**
 * MOVED OUTSIDE: Defining PortfolioItem here prevents focus loss.
 * Using React.memo for performance optimization.
 */
const PortfolioItem = React.memo(({ port, number, formData, handleInputChange, handleImageUpload, uploadingImage }) => {
  // Use local state for inputs to ensure typing is 100% smooth without lag
  const [localTitle, setLocalTitle] = useState(formData[`${port}_title`] || '');
  const [localSubtitle, setLocalSubtitle] = useState(formData[`${port}_subtitle`] || '');
  const [localDesc, setLocalDesc] = useState(formData[`${port}_shortdesc`] || '');
  const [localLink, setLocalLink] = useState(formData[`${port}_link`] || '');

  // Keep local state in sync if parent data changes (e.g., after initial fetch)
  useEffect(() => {
    setLocalTitle(formData[`${port}_title`] || '');
    setLocalSubtitle(formData[`${port}_subtitle`] || '');
    setLocalDesc(formData[`${port}_shortdesc`] || '');
    setLocalLink(formData[`${port}_link`] || '');
  }, [formData, port]);

  // Handle typing changes with local state + immediate update to parent
  const onFieldChange = (field, value, setter) => {
    setter(value);
    handleInputChange(port, field, value);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && e.target.type !== 'textarea') {
      e.preventDefault();
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-shadow">
      <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <FolderOpen className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          Portfolio Item {number}
        </h3>
        <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
          ID: {port}
        </span>
      </div>
      
      {/* Image Upload */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Feature Image
        </label>
        <div className="flex items-center gap-4">
          {formData[`${port}_image`] && (
            <div className="relative group">
              <img 
                src={`${process.env.REACT_APP_API_URL || 'https://api.marketmaestroovip.in'}${formData[`${port}_image`]}`}
                alt="Preview"
                className="w-24 h-24 object-cover rounded-lg border-2 border-gray-200 dark:border-gray-600"
              />
              <button
                type="button"
                onClick={() => handleInputChange(port, 'image', '')}
                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition shadow-md opacity-0 group-hover:opacity-100"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          <label className="cursor-pointer bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg transition flex items-center gap-2">
            <Upload className="w-4 h-4" />
            {uploadingImage.port === port && uploadingImage.uploading ? (
              <Loader className="w-4 h-4 animate-spin" />
            ) : (
              formData[`${port}_image`] ? 'Change Image' : 'Upload Image'
            )}
            <input
              type="file"
              className="hidden"
              accept="image/*"
              onChange={(e) => handleImageUpload(port, e.target.files[0])}
              disabled={uploadingImage.uploading}
            />
          </label>
        </div>
      </div>
      
      {/* Title */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
          <Type className="w-4 h-4" />
          Title
        </label>
        <input
          type="text"
          value={localTitle}
          onChange={(e) => onFieldChange('title', e.target.value, setLocalTitle)}
          onKeyDown={handleKeyDown}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
          placeholder="Enter portfolio title"
        />
      </div>
      
      {/* Subtitle */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
          <Hash className="w-4 h-4" />
          Subtitle
        </label>
        <input
          type="text"
          value={localSubtitle}
          onChange={(e) => onFieldChange('subtitle', e.target.value, setLocalSubtitle)}
          onKeyDown={handleKeyDown}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
          placeholder="Enter subtitle"
        />
      </div>
      
      {/* Short Description */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
          <AlignLeft className="w-4 h-4" />
          Short Description
        </label>
        <textarea
          value={localDesc}
          onChange={(e) => onFieldChange('shortdesc', e.target.value, setLocalDesc)}
          rows="3"
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-900 text-gray-900 dark:text-white resize-y"
          placeholder="Enter a brief description of the portfolio item"
        />
      </div>
      
      {/* Link */}
      <div className="mb-2">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
          <LinkIcon className="w-4 h-4" />
          Link URL
        </label>
        <input
          type="url"
          value={localLink}
          onChange={(e) => onFieldChange('link', e.target.value, setLocalLink)}
          onKeyDown={handleKeyDown}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
          placeholder="https://example.com/project"
        />
      </div>
    </div>
  );
});

const AdminPortfolio = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [formData, setFormData] = useState({
    port1_image: '', port1_title: '', port1_subtitle: '', port1_shortdesc: '', port1_link: '',
    port2_image: '', port2_title: '', port2_subtitle: '', port2_shortdesc: '', port2_link: '',
    port3_image: '', port3_title: '', port3_subtitle: '', port3_shortdesc: '', port3_link: '',
    port4_image: '', port4_title: '', port4_subtitle: '', port4_shortdesc: '', port4_link: '',
    port5_image: '', port5_title: '', port5_subtitle: '', port5_shortdesc: '', port5_link: '',
    port6_image: '', port6_title: '', port6_subtitle: '', port6_shortdesc: '', port6_link: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [uploadingImage, setUploadingImage] = useState({ port: null, uploading: false });

  useEffect(() => {
    if (sidebarOpen && window.innerWidth < 1024) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => { document.body.style.overflow = 'auto'; };
  }, [sidebarOpen]);

  useEffect(() => {
    fetchPortfolioData();
  }, []);

  const fetchPortfolioData = async () => {
    setLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://api.marketmaestroovip.in';
      const response = await axios.get(`${apiUrl}/api/portfolio-section`);
      if (response.data.success && response.data.data) {
        setFormData(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching portfolio data:', error);
      setMessage({ type: 'error', text: 'Failed to load portfolio data' });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = useCallback((port, field, value) => {
    setFormData(prev => ({
      ...prev,
      [`${port}_${field}`]: value
    }));
  }, []);

  const handleImageUpload = async (port, file) => {
    if (!file) return;
    const formDataUpload = new FormData();
    formDataUpload.append('image', file);
    setUploadingImage({ port, uploading: true });
    
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://api.marketmaestroovip.in';
      const response = await axios.post(`${apiUrl}/api/upload-portfolio-image`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (response.data.success) {
        handleInputChange(port, 'image', response.data.imageUrl);
        setMessage({ type: 'success', text: 'Image uploaded successfully!' });
        setTimeout(() => setMessage(null), 3000);
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      setMessage({ type: 'error', text: 'Failed to upload image' });
      setTimeout(() => setMessage(null), 3000);
    } finally {
      setUploadingImage({ port: null, uploading: false });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://api.marketmaestroovip.in';
      const response = await axios.put(`${apiUrl}/api/portfolio-section`, formData);
      if (response.data.success) {
        setMessage({ type: 'success', text: 'Portfolio section updated successfully!' });
        setTimeout(() => setMessage(null), 3000);
      }
    } catch (error) {
      console.error('Error saving portfolio data:', error);
      setMessage({ type: 'error', text: 'Failed to save portfolio data' });
      setTimeout(() => setMessage(null), 3000);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="flex h-screen">
          <Sidebar isOpen={sidebarOpen} />
          <div className="flex-1 flex flex-col min-h-screen overflow-hidden lg:ml-64">
            <Header toggleSidebar={() => setSidebarOpen(!sidebarOpen)} />
            <main className="flex-1 overflow-y-auto p-4 md:p-6 flex items-center justify-center">
              <div className="text-center">
                <Loader className="w-12 h-12 text-blue-600 dark:text-blue-400 animate-spin mx-auto mb-4" />
                <p className="text-gray-600 dark:text-gray-400">Loading portfolio data...</p>
              </div>
            </main>
            <Footer />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="flex h-screen">
        <Sidebar isOpen={sidebarOpen} />
        <div className="flex-1 flex flex-col min-h-screen overflow-hidden lg:ml-64">
          <Header toggleSidebar={() => setSidebarOpen(!sidebarOpen)} />
          <main className="flex-1 overflow-y-auto p-4 md:p-6">
            <form onSubmit={handleSubmit}>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 md:mb-8">
                <div>
                  <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white">
                    Portfolio Management
                  </h1>
                  <p className="text-sm md:text-base text-gray-600 dark:text-gray-400 mt-1 md:mt-2">
                    Manage your portfolio items - Add, edit, or update portfolio content
                  </p>
                </div>
                <button
                  type="submit"
                  disabled={saving}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold flex items-center gap-2 transition disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
                >
                  {saving ? <Loader className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
                  {saving ? 'Saving Changes...' : 'Save All Changes'}
                </button>
              </div>

              {message && (
                <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
                  message.type === 'success' 
                    ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400'
                    : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400'
                }`}>
                  {message.type === 'success' ? <CheckCircle className="w-5 h-5 flex-shrink-0" /> : <AlertCircle className="w-5 h-5 flex-shrink-0" />}
                  <p>{message.text}</p>
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {[1, 2, 3, 4, 5, 6].map((num) => (
                  <PortfolioItem 
                    key={`port${num}`}
                    port={`port${num}`} 
                    number={num} 
                    formData={formData}
                    handleInputChange={handleInputChange}
                    handleImageUpload={handleImageUpload}
                    uploadingImage={uploadingImage}
                  />
                ))}
              </div>
            </form>
          </main>
          <Footer />
        </div>
      </div>
    </div>
  );
};

export default AdminPortfolio;